/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Copyright (c) 2025 Bytedance, Inc. and its affiliates.
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  Tool,
  ToolCallEngine,
  ParsedModelResponse,
  PrepareRequestContext,
  AgentSingleLoopReponse,
  MultimodalToolCallResult,
  ChatCompletionMessageParam,
  ChatCompletionCreateParams,
  StreamProcessingState,
  StreamChunkResult,
  ChatCompletionChunk,
} from '@multimodal/agent-interface';
import { buildToolCallResultMessages } from './utils';

/**
 * Base implementation of ToolCallEngine with common functionality
 * 
 * This abstract base class provides shared implementations for common operations
 * across different tool call engines, reducing code duplication and improving maintainability.
 */
export abstract class BaseToolCallEngine extends ToolCallEngine {
  /**
   * Build historical tool call result messages for non-native engines
   * 
   * This is the common implementation used by both PromptEngineering and StructuredOutputs engines
   * since they both format tool results as user messages rather than tool messages.
   */
  protected buildNonNativeToolCallResultMessages(
    toolResults: MultimodalToolCallResult[],
  ): ChatCompletionMessageParam[] {
    return buildToolCallResultMessages(toolResults, false);
  }

  /**
   * Build historical tool call result messages for native engines
   * 
   * This implementation is used by native engines that support OpenAI's tool protocol
   * with separate tool and user message roles.
   */
  protected buildNativeToolCallResultMessages(
    toolResults: MultimodalToolCallResult[],
  ): ChatCompletionMessageParam[] {
    return buildToolCallResultMessages(toolResults, true);
  }

  /**
   * Create a basic streaming processing state
   * 
   * Provides a default implementation that can be extended by specific engines
   */
  initStreamProcessingState(): StreamProcessingState {
    return {
      contentBuffer: '',
      toolCalls: [],
      reasoningBuffer: '',
      finishReason: null,
    };
  }

  /**
   * Extract reasoning content from chunk delta
   * 
   * Common logic for processing reasoning content across different engines
   */
  protected extractReasoningContent(delta: any): string {
    // Handle reasoning content if present (non-standard OpenAI field)
    return delta?.reasoning_content || '';
  }

  /**
   * Update finish reason from chunk
   * 
   * Common logic for updating finish reason from streaming chunks
   */
  protected updateFinishReason(
    chunk: ChatCompletionChunk,
    state: StreamProcessingState,
  ): void {
    if (chunk.choices[0]?.finish_reason) {
      state.finishReason = chunk.choices[0].finish_reason;
    }
  }

  /**
   * Create a basic stream chunk result
   * 
   * Helper method to create StreamChunkResult with common structure
   */
  protected createStreamChunkResult(
    content: string = '',
    reasoningContent: string = '',
    hasToolCallUpdate: boolean = false,
    toolCalls: any[] = [],
    streamingToolCallUpdates?: any,
  ): StreamChunkResult {
    return {
      content,
      reasoningContent,
      hasToolCallUpdate,
      toolCalls,
      streamingToolCallUpdates,
    };
  }
}
